import { useState, useEffect, useCallback, useRef } from 'react';
import { attachmentsAPI } from '../utils/api';

import { useToast, apiError, useConfirm } from '../components/Toast';
import { Download, Paperclip, Trash2 } from 'lucide-react';

const PaperclipIc = () => <Paperclip size={14} />;
const DownloadIc = () => <Download size={13} />;
const TrashIc = () => <Trash2 size={13} />;

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024*1024) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1024/1024).toFixed(1)} MB`;
};

/**
 * Drop-in attachments panel. Usage:
 *   <AttachmentsPanel entityType="task" entityId={task.id} />
 *   <AttachmentsPanel entityType="project" entityId={project.id} />
 */
export default function AttachmentsPanel({ entityType, entityId, currentUserId }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await attachmentsAPI.getAll(entityType, entityId);
      setFiles(r.data);
    } catch(e) { toast.error(apiError(e, 'Upload failed. Check file size and format.')); } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await attachmentsAPI.upload(entityType, entityId, formData);
      load();
    } catch(err) { toast.error(apiError(err)); toast.warning('Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const handleDownload = async (f) => {
    const r = await attachmentsAPI.download(f.id);
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = f.file_name; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Delete this file?')) return;
    await attachmentsAPI.delete(id);
    load();
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color:'var(--text)' }}>
          <PaperclipIc /> Attachments {files.length > 0 && <span className="text-xs" style={{ color:'var(--text-secondary)' }}>({files.length})</span>}
        </h3>
        <label className="btn-secondary text-xs cursor-pointer flex items-center gap-1.5">
          {uploading ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"/> : <PaperclipIc/>}
          {uploading ? 'Uploading...' : 'Add File'}
          <input ref={inputRef} type="file" className="hidden" onChange={handleUpload} disabled={uploading}/>
        </label>
      </div>

      {loading ? (
        <p className="text-xs text-center py-4" style={{ color:'var(--text-secondary)' }}>Loading...</p>
      ) : files.length === 0 ? (
        <p className="text-xs text-center py-4" style={{ color:'var(--text-secondary)' }}>No attachments yet</p>
      ) : (
        <div className="space-y-1.5">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-2.5 p-2 rounded-lg" style={{ background:'var(--surface-alt)' }}>
              <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background:'#2E539F' }}>
                {f.file_name.split('.').pop()?.slice(0,3).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate" style={{ color:'var(--text)' }}>{f.file_name}</p>
                <p className="text-xs" style={{ color:'var(--text-secondary)' }}>
                  {formatSize(f.file_size)} &middot; {f.uploaded_by_name} &middot; {new Date(f.created_at).toLocaleDateString()}
                </p>
              </div>
              <button onClick={()=>handleDownload(f)} className="p-1.5 rounded hover:bg-[var(--border)]" style={{ color:'var(--text-secondary)' }}><DownloadIc/></button>
              {(f.uploaded_by===currentUserId) && (
                <button onClick={()=>handleDelete(f.id)} className="p-1.5 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"><TrashIc/></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
